import { AddToCartButton, Image, useLocalization, useProductOptions } from '@shopify/hydrogen'
import { AnimatePresence, motion } from 'framer-motion'
import { Dispatch, FormEvent, SetStateAction, useContext, useState } from 'react'
import { HeaderContext } from '../header/header-provider.client'
import { useI18n } from '../i18n.client'
import { ProductLayoutContext } from './product-layout-provider.client'
import styles from '../../assets/scss/components/products/product-button.module.scss'
import { createPortal } from 'react-dom'
import { classNames } from '@shopify/css-utilities'
import { useTracking } from '../../lib/hooks/tracking.client'

export const ProductButton: React.FC<{
  className: string
}> = ({ className }) => {
  const [i18n] = useI18n()
  const { setIsCartDrawerOpen } = useContext(HeaderContext)
  const { isTouched } = useContext(ProductLayoutContext)
  const { selectedVariant } = useProductOptions()
  const isOutOfStock = !selectedVariant?.availableForSale ?? false
  const { trackEvent } = useTracking()
  const { product } = useContext(ProductLayoutContext)

  if (!selectedVariant) {
    return null
  }

  if (isOutOfStock) {
    return <BackInStockButton className={className} />
  }

  if (!isTouched) {
    return null
  }

  const handleClick = () => {
    const priceProduct = product.variants?.nodes[0].priceV2.amount ?? ""
    const priceVariant = selectedVariant.priceV2?.amount ?? ""
    const currency = selectedVariant.priceV2?.currencyCode ?? ""
    const variant = selectedVariant.selectedOptions?.[0]?.value ?? "";
    const category = product.primaryCollection?.reference.title ?? ""
    const title = product.title ?? ""
    const id = selectedVariant.id?.split("/").pop() ?? ""
    const item = {
      'value': priceProduct,
      'currency': currency,
      'items': {
        'item_name': title,
        'item_id': id,
        'price': priceVariant,
        'quantity': 1,
        'item_category': category,
        'item_variant': variant
      }
    }
    trackEvent('add_to_cart', item)
    setIsCartDrawerOpen(true)
  }

  return (
    <AddToCartButton className={className} onClick={handleClick} variantId={selectedVariant?.id as string}>
      {i18n.translate('make-it-mine')}
    </AddToCartButton>
  )
}

const BackInStockButton: React.FC<{ className: string }> = ({ className }) => {
  const [i18n] = useI18n()
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <button className={className} onClick={() => setIsModalOpen(true)}>
        {i18n.translate('notify-me-when-available')}
      </button>
      <BackInStockModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
    </>
  )
}

const BackInStockModal: React.FC<{
  isModalOpen: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
}> = ({ isModalOpen, setIsModalOpen }) => {
  const {
    language: { isoCode: language },
  } = useLocalization()
  const [i18n] = useI18n()
  const { product } = useContext(ProductLayoutContext)
  const { selectedVariant } = useProductOptions()
  const [emailError, setEmailError] = useState<string>()
  const [isComplete, setIsComplete] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)

  if (typeof window !== 'object') {
    return null
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setEmailError(undefined)
    setIsDisabled(true)

    if (event.currentTarget.email.validity.valid) {
      const postUrl = new URL(window.location.origin + '/api/klaviyo/subscribe-to-back-in-stock')
      await fetch(postUrl, {
        body: JSON.stringify({
          email: event.currentTarget.email.value,
          language,
          variant: selectedVariant?.id?.replace('gid://shopify/ProductVariant/', ''),
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          revision: '2025-01-15',
        },
        method: 'POST',
      })

      setIsComplete(true)
    } else {
      setEmailError(i18n.translate('yup.string.email', { path: i18n.translate('email') }))
    }

    setIsDisabled(false)
  }

  return createPortal(
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          animate={{ opacity: 1 }}
          className={styles.overlay}
          exit={{ opacity: 0, transition: { ease: [1, 0, 1, 1] } }}
          initial={{ opacity: 0 }}
          onClick={() => setIsModalOpen(false)}
          transition={{ ease: [0, 0, 0, 1] }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.closeContainer}>
              <Image
                alt={i18n.translate('close')}
                className={styles.close}
                height={13}
                onClick={() => setIsModalOpen(false)}
                src="/cross.svg"
                width={13}
              />
            </div>
            <div className={styles.contents}>
              <div className={styles.title}>{product.title}</div>
              <div className={styles.description}>
                {i18n.translate('register-to-receive-a-notification-when-this-item')}
              </div>
              <form noValidate onSubmit={handleSubmit}>
                <input
                  autoComplete="email"
                  autoFocus
                  name="email"
                  className={classNames(styles.textInput, emailError && styles.hasError)}
                  placeholder={`${i18n.translate('email')}*`}
                  required
                  type="email"
                />
                <div className={styles.error}>{emailError}</div>
                {isComplete ? (
                  <div className={styles.complete}>
                    {i18n.translate('youre-in-well-let-you-know-when-its-back-in-stock')}
                  </div>
                ) : (
                  <button className={styles.button} disabled={isDisabled} type="submit">
                    {i18n.translate('notify-me-when-available')}
                  </button>
                )}
              </form>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
